/**
 * Metal/Color Picker for Product Variants
 *
 * Discovers sibling products (same product, different metal/color) via Shopify
 * search API and renders a swatch picker on product pages.
 *
 * Products must follow naming convention: "Product Name - Color/Metal"
 * e.g., "Franco Chain - Gold", "Franco Chain - Silver"
 */

!(function () {
  'use strict';

  // ==========================================================================
  // CONFIGURATION
  // ==========================================================================

  const CACHE_KEY_PREFIX = 'metal-picker-';
  const CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes

  // Color keyword -> gradient mapping (check compound colors first)
  const SWATCH_GRADIENTS = [
    ['white-gold', 'linear-gradient(135deg, #fafafa 0%, #e5e5e5 50%, #d0d0d0 100%)'],
    ['ancient-gold', 'linear-gradient(135deg, #c9a227 0%, #8b6914 50%, #5c4a0e 100%)'],
    ['ancient-silver', 'linear-gradient(135deg, #a8a8a8 0%, #888 50%, #666 100%)'],
    ['black', 'linear-gradient(135deg, #2d2d2d 0%, #1a1a1a 50%, #0d0d0d 100%)'],
    ['gold', 'linear-gradient(135deg, #f5e6a3 0%, #d4af37 50%, #b8960c 100%)'],
    ['white', 'linear-gradient(135deg, #fafafa 0%, #f0f0f0 50%, #e5e5e5 100%)'],
    ['silver', 'linear-gradient(135deg, #e8e8e8 0%, #c0c0c0 50%, #a8a8a8 100%)'],
    ['ruby', 'linear-gradient(135deg, #e63946 0%, #9d0208 50%, #6a040f 100%)'],
    ['diamond', 'linear-gradient(135deg, #b8c5d6 0%, #8fa3b8 50%, #6b8299 100%)'],
    ['ancient', 'linear-gradient(135deg, #c9a227 0%, #8b6914 50%, #5c4a0e 100%)'],
    ['navy', 'linear-gradient(135deg, #1e3a5f 0%, #0d1f33 50%, #071019 100%)'],
    ['spruce', 'linear-gradient(135deg, #4a6741 0%, #2d4a2a 50%, #1a2e18 100%)'],
    ['beige', 'linear-gradient(135deg, #f5f0e6 0%, #d4c9b5 50%, #b8a88a 100%)'],
  ];

  const DEFAULT_SWATCH_GRADIENT = 'linear-gradient(135deg, #888 0%, #666 50%, #444 100%)';

  // Sort order for color groups (black first, then gold, white-gold, etc.)
  const COLOR_SORT_ORDER = ['black', 'gold', 'white-gold', 'silver', 'ancient'];

  // ==========================================================================
  // CACHING
  // ==========================================================================

  function getCachedData(key) {
    try {
      const cached = sessionStorage.getItem(CACHE_KEY_PREFIX + key);
      if (!cached) return null;

      const { data, timestamp } = JSON.parse(cached);
      if (Date.now() - timestamp > CACHE_DURATION_MS) {
        sessionStorage.removeItem(CACHE_KEY_PREFIX + key);
        return null;
      }
      return data;
    } catch {
      return null;
    }
  }

  function setCachedData(key, data) {
    try {
      sessionStorage.setItem(
        CACHE_KEY_PREFIX + key,
        JSON.stringify({ data, timestamp: Date.now() })
      );
    } catch {
      // Storage full or unavailable, ignore
    }
  }

  // ==========================================================================
  // HELPERS
  // ==========================================================================

  function getProductHandleFromUrl() {
    const match = window.location.pathname.match(/\/products\/([^/?#]+)/);
    return match ? match[1] : null;
  }

  function getGradientForColor(colorSlug) {
    const normalizedSlug = colorSlug.toLowerCase();
    for (const [colorKeyword, gradient] of SWATCH_GRADIENTS) {
      if (normalizedSlug.includes(colorKeyword)) {
        return gradient;
      }
    }
    return DEFAULT_SWATCH_GRADIENT;
  }

  // Extract potential base title from handle (e.g., "franco-chain-gold" -> "franco chain")
  function guessBaseTitleFromHandle(handle) {
    // Common color suffixes to strip
    const colorSuffixes = [
      '-white-gold',
      '-ancient-gold',
      '-ancient-silver',
      '-gold',
      '-silver',
      '-black',
      '-white',
      '-ruby',
      '-diamond',
      '-ancient',
      '-navy',
      '-spruce',
      '-beige',
    ];

    let baseHandle = handle;
    for (const suffix of colorSuffixes) {
      if (baseHandle.endsWith(suffix)) {
        baseHandle = baseHandle.slice(0, -suffix.length);
        break;
      }
    }

    // Also try to strip size suffixes like "-3mm", "-5mm"
    baseHandle = baseHandle.replace(/-\d+mm$/, '');

    return baseHandle.replace(/-/g, ' ');
  }

  async function fetchProduct(handle) {
    try {
      const response = await fetch(`/products/${handle}.js`);
      if (!response.ok) return null;
      return await response.json();
    } catch {
      return null;
    }
  }

  async function searchForSiblings(searchQuery) {
    try {
      const encodedQuery = encodeURIComponent(searchQuery);
      const response = await fetch(
        `/search/suggest.json?q=${encodedQuery}&resources[type]=product&resources[limit]=20`
      );
      if (!response.ok) return [];

      const searchData = await response.json();
      return searchData.resources?.results?.products || [];
    } catch {
      return [];
    }
  }

  function processSiblingResults(searchResults, baseProductTitle, currentProductHandle) {
    const siblingProducts = [];
    const processedHandles = new Set();

    for (const product of searchResults) {
      if (processedHandles.has(product.handle)) continue;

      const productBaseTitle = product.title.split(' - ')[0].trim();
      if (productBaseTitle !== baseProductTitle) continue;

      processedHandles.add(product.handle);
      const colorVariant = product.title.split(' - ').slice(1).join(' - ').trim();
      if (!colorVariant) continue;

      siblingProducts.push({
        handle: product.handle,
        title: product.title,
        colorLabel: colorVariant,
        colorSlug: colorVariant.toLowerCase().replace(/\s+/g, '-'),
        isCurrentProduct: product.handle === currentProductHandle,
      });
    }

    return siblingProducts;
  }

  // ==========================================================================
  // SORTING
  // ==========================================================================

  function getColorGroupForSorting(colorSlug) {
    if (colorSlug.includes('white-gold')) return 'white-gold';
    if (colorSlug.includes('ancient-gold')) return 'ancient-gold';
    if (colorSlug.includes('ancient-silver')) return 'ancient-silver';

    for (const color of COLOR_SORT_ORDER) {
      if (colorSlug.includes(color)) return color;
    }
    return 'zzz'; // Unknown colors sort last
  }

  function extractSizeFromSlug(colorSlug) {
    const sizeMatch = colorSlug.match(/(\d+)mm/);
    return sizeMatch ? parseInt(sizeMatch[1], 10) : 0;
  }

  function sortSiblingsByColorAndSize(siblings) {
    return siblings.sort((productA, productB) => {
      const groupA = getColorGroupForSorting(productA.colorSlug);
      const groupB = getColorGroupForSorting(productB.colorSlug);

      const groupIndexA =
        COLOR_SORT_ORDER.indexOf(groupA) !== -1
          ? COLOR_SORT_ORDER.indexOf(groupA)
          : COLOR_SORT_ORDER.length;
      const groupIndexB =
        COLOR_SORT_ORDER.indexOf(groupB) !== -1
          ? COLOR_SORT_ORDER.indexOf(groupB)
          : COLOR_SORT_ORDER.length;

      if (groupIndexA !== groupIndexB) {
        return groupIndexA - groupIndexB;
      }

      const sizeA = extractSizeFromSlug(productA.colorSlug);
      const sizeB = extractSizeFromSlug(productB.colorSlug);

      if (sizeA !== sizeB) {
        if (sizeA === 0) return -1;
        if (sizeB === 0) return 1;
        return sizeA - sizeB;
      }

      return productA.colorSlug.localeCompare(productB.colorSlug);
    });
  }

  // ==========================================================================
  // HTML GENERATION
  // ==========================================================================

  function escapeHTML(str) {
    return String(str).replace(/[&<>"']/g, (c) => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
  }

  function buildPickerHTML(currentColorLabel, siblingProducts, pickerLabel) {
    const sortedSiblings = sortSiblingsByColorAndSize(siblingProducts);
    const swatchesHTML = sortedSiblings
      .map((sibling) => {
        const activeClass = sibling.isCurrentProduct ? ' is-active' : '';
        const gradientStyle = getGradientForColor(sibling.colorSlug);
        const safeHandle = escapeHTML(sibling.handle);
        const safeLabel = escapeHTML(sibling.colorLabel);
        return `<a href="/products/${safeHandle}" class="metal-color-picker__swatch${activeClass}" title="${safeLabel}" style="background:${gradientStyle};"><span class="visually-hidden">${safeLabel}</span></a>`;
      })
      .join('');

    return `<div class="metal-color-picker product__block product__block--lines block-padding"><div class="metal-color-picker__label"><span class="metal-color-picker__title">${pickerLabel}:</span><span class="metal-color-picker__current">${currentColorLabel}</span></div><div class="metal-color-picker__swatches">${swatchesHTML}</div></div>`;
  }

  function buildSkeletonHTML() {
    return `<div class="metal-color-picker--skeleton product__block product__block--lines block-padding" data-metal-picker-skeleton>
      <div class="skeleton-label"></div>
      <div class="skeleton-swatches">
        <div class="skeleton-swatch"></div>
        <div class="skeleton-swatch"></div>
        <div class="skeleton-swatch"></div>
      </div>
    </div>`;
  }

  // ==========================================================================
  // STYLES
  // ==========================================================================

  function injectPickerStyles() {
    if (document.getElementById('metal-color-picker-styles')) return;

    const styleElement = document.createElement('style');
    styleElement.id = 'metal-color-picker-styles';
    styleElement.textContent = `
      .metal-color-picker { margin: 0 0 16px; }
      .metal-color-picker__label { display: flex; align-items: center; gap: 6px; margin-bottom: 10px; font-size: var(--font-body-small); text-transform: capitalize; }
      .metal-color-picker__title { color: var(--text-dark, #1a1a1a); font-weight: 500; }
      .metal-color-picker__current { color: var(--text, #666); }
      .metal-color-picker__swatches { display: flex; gap: 16px; }
      .metal-color-picker__swatch { width: 28px; height: 28px; border-radius: 50%; border: 1px solid var(--hairline, #e5e5e5); cursor: pointer; transition: box-shadow 0.2s ease; display: block; }
      .metal-color-picker__swatch:hover, .metal-color-picker__swatch.is-active { box-shadow: 0 0 0 1px var(--bg, #fff), 0 0 0 2px var(--text-dark, #1a1a1a); }

      /* Skeleton loading */
      .metal-color-picker--skeleton { margin: 0 0 16px; }
      .metal-color-picker--skeleton .skeleton-label { height: 14px; width: 80px; background: linear-gradient(90deg, #e8e8e8 25%, #f5f5f5 50%, #e8e8e8 75%); background-size: 200% 100%; animation: metalPickerShimmer 1.5s infinite; border-radius: 4px; margin-bottom: 10px; }
      .metal-color-picker--skeleton .skeleton-swatches { display: flex; gap: 16px; }
      .metal-color-picker--skeleton .skeleton-swatch { width: 28px; height: 28px; border-radius: 50%; background: linear-gradient(90deg, #e8e8e8 25%, #f5f5f5 50%, #e8e8e8 75%); background-size: 200% 100%; animation: metalPickerShimmer 1.5s infinite; }
      @keyframes metalPickerShimmer { 0% { background-position-x: -200%; } 100% { background-position-x: 200%; } }
    `;
    document.head.appendChild(styleElement);
  }

  // ==========================================================================
  // SKELETON MANAGEMENT
  // ==========================================================================

  function removeSkeletonLoader() {
    const skeleton = document.querySelector('[data-metal-picker-skeleton]');
    if (skeleton) {
      skeleton.remove();
    }
  }

  function mightHaveColorVariants() {
    const scriptElement = document.getElementById('metal-color-picker-script');
    return scriptElement?.getAttribute('data-has-color-variant') === 'true';
  }

  // ==========================================================================
  // INITIALIZATION
  // ==========================================================================

  async function initColorPicker() {
    const productHandle = getProductHandleFromUrl();
    if (!productHandle) return;

    injectPickerStyles();

    const insertionTarget = document
      .querySelector('.product__price__wrap')
      ?.closest('.product__block');
    if (!insertionTarget) return;

    // Check cache first for instant display
    const cachedSiblings = getCachedData(productHandle);
    if (cachedSiblings && cachedSiblings.siblings && cachedSiblings.siblings.length > 1) {
      insertionTarget.insertAdjacentHTML(
        'afterend',
        buildPickerHTML(cachedSiblings.colorLabel, cachedSiblings.siblings, 'Color')
      );
      return;
    }

    // Show skeleton only if product title suggests color variants exist
    if (mightHaveColorVariants()) {
      insertionTarget.insertAdjacentHTML('afterend', buildSkeletonHTML());
    }

    // Start both requests in parallel
    const guessedBaseTitle = guessBaseTitleFromHandle(productHandle);
    const [currentProduct, earlySearchResults] = await Promise.all([
      fetchProduct(productHandle),
      searchForSiblings(guessedBaseTitle),
    ]);

    if (!currentProduct) {
      removeSkeletonLoader();
      return;
    }

    const titleParts = currentProduct.title.split(' - ');
    if (titleParts.length < 2) {
      removeSkeletonLoader();
      return;
    }

    const baseProductTitle = titleParts[0].trim();
    const currentColorLabel = titleParts.slice(1).join(' - ').trim();

    // Process early search results first
    let siblingProducts = processSiblingResults(
      earlySearchResults,
      baseProductTitle,
      productHandle
    );

    // If early search didn't find enough, do a more accurate search
    if (
      siblingProducts.length <= 1 &&
      guessedBaseTitle.toLowerCase() !== baseProductTitle.toLowerCase()
    ) {
      const accurateSearchResults = await searchForSiblings(baseProductTitle);
      siblingProducts = processSiblingResults(
        accurateSearchResults,
        baseProductTitle,
        productHandle
      );
    }

    if (siblingProducts.length <= 1) {
      removeSkeletonLoader();
      return;
    }

    // Cache the results
    setCachedData(productHandle, {
      colorLabel: currentColorLabel,
      siblings: siblingProducts,
    });

    removeSkeletonLoader();
    insertionTarget.insertAdjacentHTML(
      'afterend',
      buildPickerHTML(currentColorLabel, siblingProducts, 'Color')
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initColorPicker);
  } else {
    initColorPicker();
  }
})();
